// src/components/theme/elements/ThemeNavCompass.js

import React, { useState } from 'react';
import { Group, Circle, Shape, Rect } from 'react-konva';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';


/**
 * ThemeNavCompassSettings Component
 * Provides UI controls to configure the ThemeNavCompass element.
 * Allows users to set the arrow color, ring color, and position (x, y).
 */
export const ThemeNavCompassSettings = ({ element, updateElement }) => {

  /**
   * Handler for X position change.
   * Updates the X position of the element.
   */
  const handleXChange = (e) => {
    const x = parseInt(e.target.value, 10);
    if (!isNaN(x) && x >= 0) {
      updateElement(element.id, 'x', x);
    }
  };

  /**
   * Handler for Y position change.
   * Updates the Y position of the element.
   */
  const handleYChange = (e) => {
    const y = parseInt(e.target.value, 10);
    if (!isNaN(y) && y >= 0) {
      updateElement(element.id, 'y', y);
    }
  };


  return (
    <div onClick={(e) => e.stopPropagation()} style={{ padding: '10px', fontFamily: 'Arial, sans-serif' }}>
      {/* X Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        X Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_WIDTH - 1}
          value={element.x}
          onChange={handleXChange}
          style={{ marginLeft: '10px', width: '80px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
          aria-label="Set X Position"
        />
      </label>

      {/* Y Position */}
      <label style={{ display: 'block', marginTop: '10px' }}>
        Y Position:
        <input
          className={styles.inputNumber}
          type="number"
          min="0"
          max={SCREEN_HEIGHT - 1}
          value={element.y}
          onChange={handleYChange}
          style={{ marginLeft: '10px', width: '80px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
          aria-label="Set Y Position"
        />
      </label>

      {/* Arrow Color */}
      <CustomColorPicker
        label="Arrow Color"
        color={element.arrowColor}
        onChange={(value) => { updateElement(element.id, 'arrowColor', value); }}
      />

      {/* Ring Color */}
      <CustomColorPicker
        label="Ring Color"
        color={element.ringColor}
        onChange={(value) => { updateElement(element.id, 'ringColor', value); }}
      />

    </div>
  );
};

ThemeNavCompassSettings.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    arrowColor: PropTypes.string, // Arrow Color (optional) - Default to red
    ringColor: PropTypes.string,  // Ring Color (optional) - Default to black
    isEnabled: PropTypes.bool,    // Enabled state (optional)
  }).isRequired,
  updateElement: PropTypes.func.isRequired,
};

///////////////////////////////////////////////////////


const ThemeNavCompass = ({
  element,
  selectedElement,
  setSelectedElement,
  updateElement,
  gridSize,
  render, editAllowed,
  snapToGridEnabled,
  scale,
  screenWidth,
  screenHeight
}) => {
  // Fixed dimensions for the compass icon
  const fixedWidth = 40;
  const fixedHeight = 40;

  // Determine if this element is selected
  const isSelected = selectedElement && selectedElement.id === element.id;

  /**
   * Handler for drag end event.
   * Updates the X and Y positions of the element.
   */
  const handleDragEnd = (e) => {
    const { x, y } = e.target.position();

    let newX = Math.round(x);
    let newY = Math.round(y);

    // Snap to grid if enabled
    newX = snapToGridFn(newX, gridSize, snapToGridEnabled);
    newY = snapToGridFn(newY, gridSize, snapToGridEnabled);

    // Clamp the positions within the screen bounds
    newX = clamp(newX, 0, screenWidth - fixedWidth);
    newY = clamp(newY, 0, screenHeight - fixedHeight);

    updateElement(element.id, 'x', newX);
    updateElement(element.id, 'y', newY);
  };


  /**
   * Function to draw the 4-point arrow.
   */
  const drawArrow = (context, shape) => {
    context.beginPath();
    // Define the 4 points of the arrow relative to (20, 20)
    context.moveTo(20, 6);   // Tip
    context.lineTo(25, 34);  // Right
    context.lineTo(20, 27);  // Bottom
    context.lineTo(15, 34);  // Left
    context.closePath();
    context.fillStrokeShape(shape);
  };

  return (
    <Group
      x={element.x}
      y={element.y}
      draggable={!render && editAllowed} // Make draggable only when render is false
      dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, screenWidth, screenHeight, fixedWidth, fixedHeight) }}
      onClick={() => setSelectedElement(element)}
      onTap={() => setSelectedElement(element)}
      onDragEnd={handleDragEnd}
      aria-label="NavCompass Indicator"
    >
      {/* Draw Outer Ring */}
      <Circle
        x={20}
        y={20}
        radius={19}
        stroke={element.ringColor || '#000000'} // Ring Color
        strokeWidth={1}
      />

      {/* Draw Inner Ring */}
      <Circle
        x={20}
        y={20}
        radius={18}
        stroke={element.ringColor || '#000000'} // Ring Color
        strokeWidth={1}
      />

      {/* Draw Compass Arrow */}
      <Shape
        sceneFunc={drawArrow}
        fill={element.arrowColor || '#ff0000'} // Arrow Color
        stroke={null}
        rotation={0}
      />

      {/* Add the debug blue rectangle */}
      {!render && (
        <Rect
          x={0}
          y={0}
          width={fixedWidth}
          height={fixedHeight}
          stroke="#1976d2"
          strokeWidth={0.5}
          dash={isSelected ? [1, 1] : [0, 0]}
          listening={false} // Ensure it doesn't capture pointer events
        />
      )}
    </Group>
  );
};

ThemeNavCompass.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    arrowColor: PropTypes.string, // Arrow Color (optional)
    ringColor: PropTypes.string,  // Ring Color (optional)
    isEnabled: PropTypes.bool,    // Enabled state (optional)
  }).isRequired,
  selectedElement: PropTypes.shape({
    id: PropTypes.string.isRequired,
    // ... other properties as needed
  }),
  setSelectedElement: PropTypes.func.isRequired,
  updateElement: PropTypes.func.isRequired,
  gridSize: PropTypes.number.isRequired, // Assuming gridSize is required
  snapToGridEnabled: PropTypes.bool.isRequired, // Assuming snapToGridEnabled is required
  screenWidth: PropTypes.number.isRequired,
  screenHeight: PropTypes.number.isRequired,
  scale: PropTypes.number,
  render: PropTypes.bool.isRequired,
  editAllowed: PropTypes.bool,
};

export default ThemeNavCompass;